import { Link } from "react-router-dom";

export default function HowMuchShouldISaveForRetirement() {
    return (
        <>
            <p>
                The honest answer is: more than you probably are, and the exact number depends on when you start, how much you
                earn, and what you actually want retirement to look like. The rules of thumb floating around — "save 10% of your
                income" or "you need a million dollars" — aren't useless, but they hide too much. Let's get into the real math.
            </p>

            <h2>Start With What Retirement Costs</h2>
            <p>
                Most financial planners use 70–80% of your pre-retirement income as a target for annual retirement spending.
                The logic: your mortgage may be paid off, you're no longer saving for retirement, and some work-related expenses
                disappear. Whether 70% is right for you depends on your lifestyle — some people spend more in early retirement
                traveling; others spend less.
            </p>
            <p>
                If you currently earn $80,000/year and want to replace 75% of that, you need $60,000/year in retirement income.
                Social Security might cover $18,000–$24,000 of that depending on your earnings history. That leaves $36,000–
                $42,000 per year that your savings need to produce.
            </p>

            <h2>The 4% Rule and What It Implies</h2>
            <p>
                The 4% rule is the most widely used retirement withdrawal guideline. It says you can withdraw 4% of your
                portfolio in year one, adjust for inflation each year, and have a high probability of your money lasting 30
                years. It's a guideline, not a guarantee, but it gives us a useful target.
            </p>
            <p>
                To produce $40,000/year in withdrawals, you need $40,000 ÷ 0.04 = $1,000,000 saved.
                To produce $60,000/year, you need $1,500,000.
                To produce $80,000/year, you need $2,000,000.
            </p>
            <p>
                These are portfolio targets, not including Social Security. Subtract your expected Social Security benefit
                before calculating. If you expect $24,000/year from Social Security and need $60,000/year total, your savings
                only need to produce $36,000, meaning you need $900,000 — not $1,500,000.
            </p>
            <p>
                Use the <Link to="/calculator/retirement">retirement calculator</Link> to see how your current savings rate,
                expected return, and timeline interact with each other.
            </p>

            <h2>How Much to Save Each Month</h2>
            <p>
                The earlier you start, the less you need to save each month. This isn't motivation talk — it's compounding math.
            </p>
            <p>
                Scenario: You want $1,000,000 by age 65. You earn a 7% average annual return (a reasonable assumption for a
                diversified stock-heavy portfolio over long periods).
            </p>
            <p>
                Starting at 25: Save $381/month.<br />
                Starting at 35: Save $820/month.<br />
                Starting at 45: Save $1,920/month.<br />
                Starting at 55: Save $5,778/month.
            </p>
            <p>
                Waiting 10 years more than doubles what you need to save monthly. Waiting 20 years makes it 5x harder. This is
                why the most valuable thing a 25-year-old can do financially is start saving anything — even $200/month — and
                increase it as income grows.
            </p>

            <h2>Where to Save It</h2>
            <p>
                The account you use matters almost as much as the amount. The right order for most people:
            </p>
            <p>
                <strong>1. Employer 401(k) up to the match.</strong> This is free money. A 50% match on 6% of salary is a 50%
                instant return. Nothing beats it. Contribute at least enough to get the full match.
            </p>
            <p>
                <strong>2. Roth IRA (if eligible).</strong> In 2026, you can contribute $7,000/year ($8,000 if 50+) if your
                income is below the phase-out threshold ($150,000 single, $236,000 married). Roth growth is tax-free and
                withdrawals in retirement are tax-free — a major advantage if you expect to be in the same or higher tax bracket
                in retirement.
            </p>
            <p>
                <strong>3. Max the 401(k).</strong> The 2026 contribution limit is $23,500 ($31,000 if 50+). If you can afford
                it, max it. If you can't, get as close as you can.
            </p>
            <p>
                <strong>4. Taxable brokerage account.</strong> Once you've maxed tax-advantaged accounts, a standard brokerage
                account with low-cost index funds is the next step.
            </p>

            <h2>The "Am I Behind?" Check</h2>
            <p>
                Fidelity's retirement savings benchmarks are widely referenced:
            </p>
            <p>
                By 30: 1x your annual salary saved<br />
                By 40: 3x your salary<br />
                By 50: 6x your salary<br />
                By 60: 8x your salary<br />
                By 67: 10x your salary
            </p>
            <p>
                These are targets, not verdicts. If you're at 35 with $50,000 saved on a $70,000 salary, you're behind the 3x
                benchmark. That doesn't mean you're doomed — it means you need to be more aggressive about saving than someone
                who started earlier.
            </p>

            <h2>Adjusting for Reality</h2>
            <p>
                A few variables that change these numbers:
            </p>
            <p>
                <strong>Pension or defined benefit plan:</strong> If your employer offers one, it reduces how much you need to
                save independently. Get the projected benefit amount and work backward from there.
            </p>
            <p>
                <strong>Part-time work in early retirement:</strong> Even $15,000/year in income from consulting or part-time
                work significantly reduces portfolio pressure and extends how long your money lasts.
            </p>
            <p>
                <strong>Healthcare costs:</strong> Before Medicare at 65, healthcare is a significant expense. If you retire at
                60, budget $800–$1,500/month in premiums and out-of-pocket costs until Medicare kicks in.
            </p>

            <h2>The Bottom Line</h2>
            <p>
                There's no single right answer, but the numbers above give you a framework to find your target. Calculate your
                retirement income need, subtract Social Security, and back-calculate the portfolio size you need. Then run the
                monthly savings math using the{" "}
                <Link to="/calculator/retirement">retirement calculator</Link>. The result might be uncomfortable — but knowing
                is always better than guessing.
            </p>
        </>
    );
}